<template>
  <Teleport to="body">
    <!-- Top-centre, above the sheet: the one toast that matters most fires from
         inside the open item sheet, which owns the bottom half of the screen, so
         a bottom toast would surface behind it. aria-live so it is spoken, not
         only seen. -->
    <div
      aria-live="polite"
      class="pointer-events-none fixed inset-x-0 top-0 z-toast flex flex-col items-center gap-2 px-4 pt-[max(env(safe-area-inset-top),0.75rem)]"
      role="status"
    >
      <TransitionGroup
        enter-active-class="duration-sheet ease-pj"
        enter-from-class="-translate-y-3 opacity-0 motion-reduce:translate-y-0"
        leave-active-class="duration-exit ease-pj"
        leave-to-class="-translate-y-3 opacity-0 motion-reduce:translate-y-0"
        move-class="duration-sheet ease-pj"
      >
        <!-- A hairline, not a shadow — the same reason PjSheet carries one: in
             light the panel and the page are the same white, and the border is
             what makes this an object. -->
        <button
          v-for="toast in store.toasts"
          :key="toast.id"
          class="pj-tap pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-m border bg-card px-4 py-3 text-left"
          type="button"
          @click="store.dismiss(toast.id)"
        >
          <PjIcon
            class="shrink-0 text-muted"
            :name="iconFor(toast.tone)"
          />
          <span class="min-w-0 flex-1 text-body text-ink">{{ toast.message }}</span>
        </button>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import type { PjToastTone } from '@/domains/pjazza/store/use-toast-store/useToastStore.ts'

import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import { useToastStore } from '@/domains/pjazza/store/use-toast-store/useToastStore.ts'

/**
 * The one toaster, mounted once in the shell. It teleports to the body, so where
 * it sits in the layout is immaterial — every screen with chrome has it, and the
 * store is how the rest of the app reaches it.
 *
 * A toast dismisses itself on a timer; a tap dismisses it early. There is no
 * close glyph — the whole thing is the target, because a 4-second message is not
 * worth a second control to aim at.
 */
const store = useToastStore()

// A tick for something done, a note for everything else. The set has no warning
// glyph on purpose: nothing here shouts.
const iconFor = (tone: PjToastTone): typeof PJ_ICON.CHECK | typeof PJ_ICON.NOTE =>
  tone === 'success' ? PJ_ICON.CHECK : PJ_ICON.NOTE
</script>
